'use client';

import MediaGrid from '@/components/media-asset/MediaGrid';
import { useCurrentApp } from '@/stores/userDataStore';
import { useCallback, useEffect, useMemo } from 'react';

import { Bleed, BlockStack, Card, EmptyState } from '@shopify/polaris';
import { useListPersonalAvatars, usePersonalAvatarCache } from './hooks/usePersonalAvatar';
import {
  PersonalAvatarPollingProvider,
  usePersonalAvatarPollingContext,
} from './components/PersonalAvatarPollingContext';
import { PersonalAvatarStatus, PersonalAvatarViewData } from '@/api/personal-avatars';
import { PersonalAvatarCard } from './components/card/PersonalAvatarCard';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import StudioRoutes from '@/utils/routes';
import { CREATE_CUSTOM_INFLUENCER } from '@/constants/text';
import EmptyGrid from '@/components/EmptyGrid/EmptyGrid';

const PersonalAvatarsListContent = () => {
  const { pushPath } = useRouterNavigation();
  const { appKey, isLoading: isUserLoading } = useCurrentApp();
  const { personalAvatars, isLoading } = useListPersonalAvatars({ appKey });
  const { setPollingQueue } = usePersonalAvatarPollingContext();

  useEffect(() => {
    if (personalAvatars && !isLoading) {
      const pendingAvatars = personalAvatars.reduce<PersonalAvatarViewData[]>((acc, avatar) => {
        if (avatar.status === PersonalAvatarStatus.PROCESSING) {
          acc.push(avatar);
        }
        return acc;
      }, []);

      if (pendingAvatars.length > 0) {
        setPollingQueue(pendingAvatars);
      }
    }

    return () => setPollingQueue([]);
  }, [isLoading, personalAvatars, setPollingQueue]);

  const renderPersonalAvatarCard = useCallback((personalAvatar: PersonalAvatarViewData) => {
    return <PersonalAvatarCard key={personalAvatar.id} avatar={personalAvatar} />;
  }, []);

  if (isLoading || isUserLoading || !appKey) {
    return <EmptyGrid staticHeight="calc(100vh - 270px)" />;
  }

  if (!personalAvatars?.length) {
    return (
      <Card>
        <BlockStack inlineAlign="center">
          <EmptyState
            heading="You don't have any custom influencers yet"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          />
        </BlockStack>
      </Card>
    );
  }

  return <MediaGrid items={personalAvatars} renderItem={renderPersonalAvatarCard} staticHeight="calc(100vh - 270px)" />;
};

export const PersonalAvatarsList = () => {
  const { appKey } = useCurrentApp();
  const { updatePersonalAvatarCache } = usePersonalAvatarCache({ appKey });

  return (
    <PersonalAvatarPollingProvider onCompletion={updatePersonalAvatarCache}>
      <PersonalAvatarsListContent />
    </PersonalAvatarPollingProvider>
  );
};
